/**
 * Create case's one press: make the case, then send the payment link, then open the case.
 *
 * Lloyd, Oct 2026: "I'm not happy that it takes 5 steps to send a payment link. This should be down
 * to 1-2." With "Send payment link by email and text" ticked on the review screen, Create makes the
 * case and sends the link in the same press, and the case opens saying what went where. The separate
 * payment step is shown only when the box was not ticked (a referral partner's client, OTR, or
 * staff's choice) or something needs attention: the link could not be made, nothing reached the
 * client, or a channel staff chose failed. It then holds the link already made, so Send there sends
 * that link and never a second one.
 *
 * Still only ever on a staff press. Nothing here runs on its own.
 */
import type { CreatedLink, PaymentLinkSender } from './paymentLinkSend';

export interface PaymentOnCreateChoice {
  /** The review screen's "Send payment link by email and text" box. */
  send: boolean;
  amountCents: number;
  /** The channels that can be used for this client (email on file; text with consent). */
  email: boolean;
  sms: boolean;
  /** The plan, recorded as the link's note as the payment step records it. */
  plan?: string;
  /** Why there is no text, to say so in the notice (e.g. "a new client has not agreed to texts yet"). */
  smsUnavailable?: string | null;
}

export interface DeliveryResult {
  sent: boolean;
  emailSent: boolean;
  smsSent: boolean;
  reason: string;
}

export type AfterCreate =
  | { kind: 'open-case'; notice: string }
  | {
      kind: 'payment-step';
      /** A link already made, for the step to send rather than making another. */
      pendingLink: CreatedLink | null;
      /** What went wrong, shown on the step; null when the box was simply not ticked. */
      problem: string | null;
      /** The channels still to send by, when one of two went. */
      channels: { email: boolean; sms: boolean } | null;
    };

function dollars(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export async function sendPaymentAfterCreate(
  ticketGuid: string,
  choice: PaymentOnCreateChoice,
  sender: PaymentLinkSender<DeliveryResult>,
): Promise<AfterCreate> {
  if (!choice.send || (!choice.email && !choice.sms)) {
    return { kind: 'payment-step', pendingLink: null, problem: null, channels: null };
  }

  let result: DeliveryResult;
  try {
    result = await sender.send({
      ticketGuid,
      amountCents: choice.amountCents,
      note: choice.plan,
      channels: { email: choice.email, sms: choice.sms },
    });
  } catch (error) {
    return {
      kind: 'payment-step',
      pendingLink: sender.pendingLink(),
      problem: `The case was created, but the payment link was not sent: ${
        error instanceof Error ? error.message : 'unknown error'
      }`,
      channels: null,
    };
  }

  const emailMissing = choice.email && !result.emailSent;
  const smsMissing = choice.sms && !result.smsSent;
  if (!result.sent || emailMissing || smsMissing) {
    return {
      kind: 'payment-step',
      pendingLink: sender.lastLink(),
      problem: `The case was created, but the payment link did not all go. ${result.reason}`,
      channels: result.sent ? { email: emailMissing, sms: smsMissing } : null,
    };
  }

  const noText = !choice.sms && choice.smsUnavailable ? ` No text: ${choice.smsUnavailable.replace(/\.$/, '')}.` : '';
  return {
    kind: 'open-case',
    notice: `Case created. Payment link for ${dollars(choice.amountCents)}: ${result.reason}${noText}`,
  };
}

/** How the screen's "Send payment link by email and text" box stands (components/SendPaymentLinkBox). */
export interface PaymentBoxDefaults {
  mode: 'ready' | 'partner' | 'blocked';
  emailUnavailable: string | null;
  smsUnavailable: string | null;
}

/**
 * Whether Create will send, what stops Create, and what to send, from the box as staff left it. One
 * rule for Create case and the New Ticket wizard: never for OTR, never with no channel, only by the
 * channels this client can be reached on, and never by email when the new client's email was not
 * saved (another client holds it).
 */
export function paymentBoxDecision(
  defaults: PaymentBoxDefaults | null,
  box: { send: boolean; amount: string; plan?: string },
  created: { emailNotSaved?: boolean } = {},
): { willSend: boolean; problem: string | null; choice: PaymentOnCreateChoice } {
  const email = Boolean(defaults && !defaults.emailUnavailable);
  const sms = Boolean(defaults && !defaults.smsUnavailable);
  const willSend = box.send && Boolean(defaults) && defaults?.mode !== 'blocked' && (email || sms);
  const dollars = Number(box.amount);
  const amountValid = box.amount.trim() !== '' && Number.isFinite(dollars) && dollars >= 0.5 && dollars <= 50000;
  return {
    willSend,
    problem: willSend && !amountValid ? 'Enter a payment amount between $0.50 and $50,000' : null,
    choice: {
      send: willSend,
      amountCents: Math.round(dollars * 100),
      email: email && !created.emailNotSaved,
      sms,
      plan: box.plan,
      smsUnavailable: defaults?.smsUnavailable ?? null,
    },
  };
}
